import { PROGRAMS, WINNERS } from '../data';
import { BRAND_NAME, DEFAULT_OG_IMAGE, toAbsoluteUrl } from './config';

type StructuredData = Record<string, unknown>;

export const PAGE_METADATA = {
  home: {
    title: `Beauty With Purpose | ${BRAND_NAME}`,
    description:
      'Discover Delight Business Empowerment, the women empowerment platform connecting beauty, leadership, grants, and mentorship for lasting impact.',
    path: '/',
    image: DEFAULT_OG_IMAGE,
  },
  faces: {
    title: `Delight Business Empowerment | ${BRAND_NAME}`,
    description:
      'Meet the Delight Business Empowerment winners shaping social impact, leadership, and business growth across Nigeria.',
    path: '/faces',
    image: DEFAULT_OG_IMAGE,
  },
  programs: {
    title: `Empowerment Programs | ${BRAND_NAME}`,
    description:
      'Explore Delight Business Empowerment programs offering startup capital, mentorship, and practical tools for women ready to grow.',
    path: '/programs',
    image: DEFAULT_OG_IMAGE,
  },
  impact: {
    title: `Impact | ${BRAND_NAME}`,
    description:
      'See the Delight Business Empowerment impact through grants, empowerment initiatives, and opportunities that help women lead and scale.',
    path: '/impact',
    image: DEFAULT_OG_IMAGE,
  },
  about: {
    title: `About Us | ${BRAND_NAME}`,
    description:
      'Learn about Delight Business Empowerment, our mission, values, and vision for beauty, entrepreneurship, and social impact in Nigeria.',
    path: '/about',
    image: DEFAULT_OG_IMAGE,
  },
  contact: {
    title: `Contact Us | ${BRAND_NAME}`,
    description:
      'Contact Delight Business Empowerment for partnerships, program support, media inquiries, and guidance on empowerment opportunities.',
    path: '/contact',
    image: DEFAULT_OG_IMAGE,
  },
  privacy: {
    title: `Privacy Policy | ${BRAND_NAME}`,
    description:
      'Read the Delight Business Empowerment privacy policy to understand how personal data, applications, and contact details are handled.',
    path: '/privacy',
    image: DEFAULT_OG_IMAGE,
  },
  terms: {
    title: `Terms of Service | ${BRAND_NAME}`,
    description:
      'Review Delight Business Empowerment terms of service for website use, program applications, and participant responsibilities.',
    path: '/terms',
    image: DEFAULT_OG_IMAGE,
  },
  cookies: {
    title: `Cookie Policy | ${BRAND_NAME}`,
    description:
      'Read the Delight Business Empowerment cookie policy to learn how cookies support site preferences, security, and performance.',
    path: '/cookies',
    image: DEFAULT_OG_IMAGE,
  },
  register: {
    title: `Register | ${BRAND_NAME}`,
    description:
      'Register for Delight Business Empowerment and apply for a beauty and empowerment contest with grants, mentorship, and visibility.',
    path: '/register',
    image: DEFAULT_OG_IMAGE,
  },
  vote: {
    title: `Vote | ${BRAND_NAME}`,
    description:
      'Vote in Delight Business Empowerment public voting and support verified contestants with one secure vote that helps amplify their journey.',
    path: '/vote',
    image: DEFAULT_OG_IMAGE,
  },
  adminLogin: {
    title: `Admin Login | ${BRAND_NAME}`,
    description:
      'Secure admin login for the Delight Business Empowerment team to review contestants, voting activity, and platform operations.',
    path: '/adminlogin',
    image: DEFAULT_OG_IMAGE,
  },
  adminDashboard: {
    title: `Admin Dashboard | ${BRAND_NAME}`,
    description:
      'Private Delight Business Empowerment dashboard for administrators managing contestants, voting metrics, and platform settings.',
    path: '/admin',
    image: DEFAULT_OG_IMAGE,
  },
} as const;

const getOrganizationSchema = (): StructuredData => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: BRAND_NAME,
  url: toAbsoluteUrl('/'),
  description:
    'A purpose-driven platform that empowers women through beauty, entrepreneurship, mentorship, and social impact opportunities.',
  email: 'hello@delightempowerment.com',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Lagos',
    addressCountry: 'NG',
  },
  areaServed: 'NG',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    email: 'hello@delightempowerment.com',
    areaServed: 'NG',
    availableLanguage: ['en'],
  },
});

export const getHomeStructuredData = (): StructuredData[] => [
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: BRAND_NAME,
    url: toAbsoluteUrl('/'),
    description: PAGE_METADATA.home.description,
    inLanguage: 'en',
  },
  getOrganizationSchema(),
];

export const getFacesStructuredData = (): StructuredData[] => [
  {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Delight Business Empowerment Winners Gallery',
    url: toAbsoluteUrl('/faces'),
    description: PAGE_METADATA.faces.description,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: WINNERS.map((winner, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: `Miss ${winner.name}`,
        description: winner.story,
        url: toAbsoluteUrl('/faces'),
      })),
    },
  },
];

export const getProgramsStructuredData = (): StructuredData[] => [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Delight Business Empowerment programs',
    url: toAbsoluteUrl('/programs'),
    description: PAGE_METADATA.programs.description,
    provider: {
      '@type': 'Organization',
      name: BRAND_NAME,
      url: toAbsoluteUrl('/'),
    },
    areaServed: 'NG',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Empowerment initiatives',
      itemListElement: PROGRAMS.map((program, index) => ({
        '@type': 'Offer',
        position: index + 1,
        itemOffered: {
          '@type': 'Service',
          name: program.title,
          description: program.longDesc,
        },
      })),
    },
  },
];

export const getImpactStructuredData = (): StructuredData[] => [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Delight Business Empowerment Impact',
    url: toAbsoluteUrl('/impact'),
    description: PAGE_METADATA.impact.description,
    about: {
      '@type': 'Organization',
      name: BRAND_NAME,
      areaServed: 'NG',
    },
  },
];

export const getAboutStructuredData = (): StructuredData[] => [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About Delight Business Empowerment',
    url: toAbsoluteUrl('/about'),
    description: PAGE_METADATA.about.description,
    mainEntity: getOrganizationSchema(),
  },
];

export const getContactStructuredData = (): StructuredData[] => [
  {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact Delight Business Empowerment',
    url: toAbsoluteUrl('/contact'),
    description: PAGE_METADATA.contact.description,
    mainEntity: getOrganizationSchema(),
  },
];

export const getRegisterStructuredData = (): StructuredData[] => [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Delight Business Empowerment Registration',
    url: toAbsoluteUrl('/register'),
    description: PAGE_METADATA.register.description,
    potentialAction: {
      '@type': 'ApplyAction',
      target: toAbsoluteUrl('/register'),
      name: 'Apply for Delight Business Empowerment',
    },
  },
];

export const getVoteStructuredData = (): StructuredData[] => [
  {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Delight Business Empowerment Public Voting',
    url: toAbsoluteUrl('/vote'),
    description: PAGE_METADATA.vote.description,
    potentialAction: {
      '@type': 'VoteAction',
      target: toAbsoluteUrl('/vote'),
      name: 'Vote for a contestant',
    },
  },
];

export const getPrivacyStructuredData = (): StructuredData[] => [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Privacy Policy',
    url: toAbsoluteUrl('/privacy'),
    description: PAGE_METADATA.privacy.description,
    isPartOf: {
      '@type': 'WebSite',
      name: BRAND_NAME,
      url: toAbsoluteUrl('/'),
    },
  },
];

export const getTermsStructuredData = (): StructuredData[] => [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Terms of Service',
    url: toAbsoluteUrl('/terms'),
    description: PAGE_METADATA.terms.description,
    isPartOf: {
      '@type': 'WebSite',
      name: BRAND_NAME,
      url: toAbsoluteUrl('/'),
    },
  },
];

export const getCookiesStructuredData = (): StructuredData[] => [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Cookie Policy',
    url: toAbsoluteUrl('/cookies'),
    description: PAGE_METADATA.cookies.description,
    isPartOf: {
      '@type': 'WebSite',
      name: BRAND_NAME,
      url: toAbsoluteUrl('/'),
    },
  },
];
